/* =============================================================================
   ics-UI SHELL ENTRY - THE TEXT FIELD (and, inside it, THE NUMERIC FIELD and
   THE NUMBER DEFINITION).
   Bundle entry -> dist-v2/ics-textbox.min.js, loaded by the page shells in the
   head. It is NOT the full library: exactly core + Registry + the Textbox
   component (js/components/ics-textbox.js) + the Numberbox / Numberspinner
   components (js/components/ics-numberbox.js, which extend Textbox) + the
   facade bridge + auto-mount, and it claims nothing else. Like every bundle
   here it owns its OWN Registry / Component key: it never sees another bundle's
   instances and vice versa.

   ⚠ IT PUBLISHES `Ics.number` - the ONLY number format / parse / convert
   definition in the product (group · parse · parseLoose · fixed · format ·
   cellFormatter · cellParser · the unit converters · the amount-in-words
   engines). Every other bundle and every view reads `globalThis.Ics.number` at
   CALL time; nothing imports ics-numberbox.js into another entry (its
   Registry.add would register the numeric field a second time there). So this
   bundle must load before any grid, picker, chart or validation code runs.

   WHAT IT DOES ON A PAGE
   1. Mounts `input[data-ics="textbox"]` markup - the one authored form of the
      plain text field on this shell - at ready time, again after any markup
      injection (grid cell renderers, .html(), window content, loaded
      fragments) through the MutationObserver in facade/mount.js, and again
      after a page-level markup re-parse `Ics.parse(ctx)` when the page
      provides one.
   2. `$.fn.textbox` becomes a DISPATCHER: an own-attribute / ics-mounted
      receiver goes to the component's bridge; every other receiver goes to the
      implementation that held the name before us, untouched. A MIXED set is
      split and each half goes to its owner (a getter reads the FIRST element's
      owner). An EMPTY set goes to ours, where a statement against an absent
      element is a silent no-op and a getter yields undefined. That
      implementation's statics (defaults/methods/parseOptions) are carried onto
      the dispatcher, because other field widgets on the page read them
      through `$.fn.textbox` at call time. On a page where nothing else defines
      the name, ours answers everything.
      HAND-OVER: an input the other implementation has CLAIMED (its engine
      stamp `<family>-f` is on the element - the family widgets stamp it before
      their own init reaches `$.fn.textbox`) is never ours: it is released
      (instance destroyed, own attribute dropped) and routed to that
      implementation, so a script that turns an own plain input into one of
      those widgets (`$('#qty').numberbox({…})`) gets a clean input and a
      working widget, whether it runs before or after the ready-time mount.
   3. `$.fn.icsTextbox` / `$.fn.icsNumberbox` / `$.fn.icsNumberspinner` are OURS
      on the page (last write wins, re-asserted in the ready queue).
   4. `$.fn.numberbox` / `$.fn.numberspinner` are WRAPPED as dispatchers over
      whatever holds them (see THE NUMERIC FAMILY DISPATCH below): an own
      `input[data-ics="numberbox"|"numberspinner"]` or mounted instance goes to
      the numeric component - an options object on an already-mounted field is
      an option-merge, so `$('#qty').numberbox({precision:2})` re-configures the
      mounted field rather than building a second one.
   5. Publishes `Ics.components.{Textbox, Numberbox, Numberspinner}`,
      `Ics.textbox` ({ isOurs, installDispatch, mount, unmount }),
      `Ics.numberbox` ({ installDispatch, dispatch, mount, unmount }) and
      `Ics.number` ADDITIVELY onto an existing `Ics`, or publishes a minimal
      `Ics` when none exists. Use `Ics.textbox.unmount(root)` to release the
      text fields under a fragment before removing it: a top-level `Ics.unmount`
      published by another bundle sees only that bundle's registry.
   CSS: the shell links lib-v2/css/components/ics-textbox.css raw (tokens are
   already on the shell; its PART N paints the numeric field). NO stylesheet is
   self-injected by this bundle.
   ============================================================================= */
import { Component } from './core/component.js';
import { Registry } from './services/registry.js';
import { makeBridge, registerAllFacades } from './facade/register.js';
import { mount, unmount, installAutoMount, installMutationMount } from './facade/mount.js';
import { Textbox } from './components/ics-textbox.js';
/* THE NUMERIC FIELD rides this bundle rather than one of its own: Numberbox
   EXTENDS Textbox, so folding costs no duplicated base class and saves a whole
   extra MutationObserver pass on every page. It ships inside
   dist-v2/ics-textbox.min.js; there is no separate numberbox bundle. */
import { Numberbox, Numberspinner, NUMBERBOX_DISPATCH, IcsNumber } from './components/ics-numberbox.js';

/* THE OWN FORM, and the one exclusion: an input another field implementation
   has CLAIMED. The other implementation on the page marks the input it wraps
   with an engine class of the shape `<family>-f` (the family first, `textbox-f`
   once the wrap stands) BEFORE it calls `$.fn.textbox(opts)` on it - that stamp
   is how a claim is recognised here. A claimed input is never mounted, and a
   claim that arrives AFTER our mount releases our instance (see isOurs). */
const OWN = 'input[data-ics="textbox"]:not(.textbox-f)';
const FOREIGN_FIELD_MARK = /(^|\s)(textbox|numberbox|numberspinner|timespinner|datetimespinner|spinner|combo|combobox|combotree|combogrid|combotreegrid|datebox|datetimebox|timepicker|passwordbox|searchbox|maskedbox|tagbox|filebox)-f(\s|$)/;

/* narrow the registration for THIS bundle: own-attribute markup only, and no
   alias claim - the plain name is taken by the dispatcher below */
const entry = Registry.get('Textbox');
if (entry) {
  entry.selector = OWN;
  entry.claimName = null;
}

/** an own date/time role: the same hand-over, declared through the own attribute
    rather than through a generated engine class */
const OWN_DATETIME_ROLE = /^(date|datetime|time|month|year)$/;

/** the receiver carries another implementation's engine stamp, or has been
    declared as one of the own date/time roles - either way it is not ours */
function claimedByOther(el) {
  if (el.getAttribute && OWN_DATETIME_ROLE.test(el.getAttribute('data-ics') || '')) return true;
  return !!(el.className && typeof el.className === 'string' && FOREIGN_FIELD_MARK.test(el.className));
}

/** hand an input over: destroy our instance if we had one, drop the own
    attribute so neither the stylesheet nor a later mount touches it again */
function release(el) {
  const inst = Component.get(el);
  if (inst instanceof Textbox) inst.destroy();
  if (el.getAttribute('data-ics') === 'textbox') el.removeAttribute('data-ics');
}

/**
 * An element the component owns: already mounted by us, or declared with the
 * own attribute - UNLESS another field implementation has claimed it, in which
 * case it is released here and the answer is false from then on. A page may
 * turn an own plain input into one of the other field widgets by script
 * (`$('#qty').numberbox({…})` after the ready-time mount); that widget's own
 * init reaches `$.fn.textbox` with the receiver already stamped, so the hand-
 * over happens exactly there and its build proceeds on a clean input.
 */
export function isOurs(el) {
  if (!el || el.nodeType !== 1) return false;
  if (claimedByOther(el)) { release(el); return false; }
  if (Component.get(el) instanceof Textbox) return true;
  return !!(el.matches && el.matches(OWN));
}

/**
 * Install the page-level `$.fn.textbox` dispatcher over whatever holds the name
 * (nothing, on a page where this bundle is the only implementation). Idempotent;
 * a name re-defined by a later script is wrapped again on the next call.
 * @returns {boolean} installed (or already installed)
 */
export function installTextboxDispatch($) {
  if (!$ || !$.fn) return false;
  const cur = $.fn.textbox;
  if (typeof cur === 'function' && cur._icsTextboxDispatch) return true;
  const ours = makeBridge($, 'Textbox', Textbox, OWN);
  const foreign = typeof cur === 'function' ? cur : null;
  const dispatch = function () {
    if (this.length === 0 || !foreign) return ours.apply(this, arguments);
    const mine = [], theirs = [];
    for (let i = 0; i < this.length; i++) (isOurs(this[i]) ? mine : theirs).push(this[i]);
    if (!theirs.length) return ours.apply(this, arguments);
    if (!mine.length) return foreign.apply(this, arguments);
    /* mixed set: each half to its owner; the return follows the FIRST element's
       owner, and a mutator hands the ORIGINAL set back so a chain still holds */
    const a = ours.apply($(mine), arguments);
    const b = foreign.apply($(theirs), arguments);
    const r = isOurs(this[0]) ? a : b;
    return (r && r.jquery) ? this : r;
  };
  if (foreign) Object.assign(dispatch, foreign);      // defaults / methods / parseOptions - read via $.fn.textbox at call time
  dispatch._icsTextboxDispatch = true;
  dispatch._icsForeign = foreign;
  dispatch._icsBridge = ours;
  $.fn.textbox = dispatch;
  return true;
}

/* -----------------------------------------------------------------------------
   THE NUMERIC FAMILY DISPATCH  ($.fn.numberbox / $.fn.numberspinner)

   ⚠ These names must be WRAPPED, never CLAIMED. `claimName` is claim-if-free,
   and on a page where another implementation already holds `numberbox` a claim
   would simply lose. The list of names lives in the COMPONENT
   (NUMBERBOX_DISPATCH) and each selector is read back OUT OF THE REGISTRY here,
   so the mount selector and the dispatch selector can never drift apart - a
   re-typed copy in this entry would drift.
   Same split semantics as the textbox dispatcher above: own receivers to us,
   everything else to whoever held the name, a mixed set split in two, and an
   empty set to ours (statement = silent no-op, getter = undefined).
   ---------------------------------------------------------------------------- */
const NUMERIC_CTORS = { Numberbox: Numberbox, Numberspinner: Numberspinner };

/** an element this numeric component owns: mounted by us, or declared with its
    own attribute - and never one another field implementation has already
    stamped and wrapped */
function isOursNumeric(el, entryName, selector) {
  if (!el || el.nodeType !== 1) return false;
  if (el.className && typeof el.className === 'string' && FOREIGN_FIELD_MARK.test(el.className)) return false;
  const Ctor = NUMERIC_CTORS[entryName];
  if (Component.get(el) instanceof Ctor) return true;
  return !!(selector && el.matches && el.matches(selector));
}

/**
 * Wrap one numeric name over whatever holds it. Idempotent per name.
 * @returns {boolean} installed (or already installed)
 */
export function installNumberboxDispatch($) {
  if (!$ || !$.fn) return false;
  for (const row of NUMBERBOX_DISPATCH) {
    const cur = $.fn[row.name];
    if (typeof cur === 'function' && cur._icsNumericDispatch) continue;
    const reg = Registry.get(row.entry);
    if (!reg) continue;                                   // the component did not register - never guess a selector
    const Ctor = NUMERIC_CTORS[row.entry];
    const sel = reg.selector;
    const ours = makeBridge($, row.entry, Ctor, sel);
    const foreign = typeof cur === 'function' ? cur : null;
    const mine = (el) => isOursNumeric(el, row.entry, sel);
    const dispatch = function () {
      if (this.length === 0 || !foreign) return ours.apply(this, arguments);
      const a = [], b = [];
      for (let i = 0; i < this.length; i++) (mine(this[i]) ? a : b).push(this[i]);
      if (!b.length) return ours.apply(this, arguments);
      if (!a.length) return foreign.apply(this, arguments);
      const ra = ours.apply($(a), arguments);
      const rb = foreign.apply($(b), arguments);
      const r = mine(this[0]) ? ra : rb;
      return (r && r.jquery) ? this : r;                  // a mutator hands the ORIGINAL set back so a chain holds
    };
    if (foreign) Object.assign(dispatch, foreign);        // defaults / methods / parseOptions, read at call time
    dispatch._icsNumericDispatch = true;
    dispatch._icsForeign = foreign;
    dispatch._icsBridge = ours;
    $.fn[row.name] = dispatch;
  }
  return true;
}


const g = globalThis;
if (typeof document !== 'undefined') {
  const $ = g.jQuery || g.$;
  if ($ && $.fn) {
    registerAllFacades($);                                    // $.fn.icsTextbox / icsNumberbox / icsNumberspinner: ours
    installTextboxDispatch($);                                // $.fn.textbox: ours for own-attribute/mounted, the prior holder otherwise
    installNumberboxDispatch($);                              // $.fn.numberbox / numberspinner: WRAPPED over whatever holds them
    $(() => {                                                 // ready-queue re-assert (same contract as the other shell entries)
      registerAllFacades($);
      installTextboxDispatch($);
      installNumberboxDispatch($);
    });
  }
  installAutoMount();                                         // ready-queue mount (before per-view glue)
  installMutationMount();                                     // post-ready injections (any path)
}

/* the entry's own helpers are published under `Ics.textbox` - a namespace of
   their own, because another shell entry publishes an `isOurs` of ITS component
   at the top level and the two must never shadow each other. mount/unmount are
   published here too: every bundle owns its OWN Registry, so a top-level
   `Ics.unmount(root)` installed by an earlier bundle cannot see this one's
   instances - `Ics.textbox.unmount(root)` is the call that destroys text fields
   under a fragment before the fragment is removed. */
const textboxTools = { isOurs, installDispatch: installTextboxDispatch, mount, unmount };
const numberboxTools = { installDispatch: installNumberboxDispatch, dispatch: NUMBERBOX_DISPATCH, mount, unmount };
const IcsTextboxEntry = {
  version: '2.2.0-textbox',
  Component,
  registry: Registry,
  components: { Textbox, Numberbox, Numberspinner },
  textbox: textboxTools,
  numberbox: numberboxTools,
  /* THE number format / parse / convert definition - the ONLY one in the
     product; every other bundle and every view reaches it HERE at call time
     (`Ics.number.format(...)`), never by import. Published unconditionally:
     last write wins, and this bundle is the one that owns it. */
  number: IcsNumber,
  mount, unmount,
};
if (!g.Ics) g.Ics = IcsTextboxEntry;                          // no namespace yet: publish the minimal one
else {                                                        // another entry published one: add to it, never replace
  g.Ics.components = Object.assign(g.Ics.components || {}, { Textbox, Numberbox, Numberspinner });
  if (!g.Ics.textbox) g.Ics.textbox = textboxTools;
  if (!g.Ics.numberbox) g.Ics.numberbox = numberboxTools;
  g.Ics.number = IcsNumber;                                   // the single definition - always this bundle's
}
export default IcsTextboxEntry;
